// import node module libraries
import React, { useMemo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Dropdown, Badge, Table, Offcanvas } from 'react-bootstrap';
import { MoreVertical, Edit, Trash } from 'react-feather';
import Moment from 'react-moment';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

// Import required custom components
import TanstackTable from 'components/elements/advance-table/TanstackTable';
// Import Loading Animation
import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
// Import authentication components
import useAuthHeader from 'react-auth-kit/hooks/useAuthHeader';

// import notification component
import Notification, { handleShowNotification } from 'components/elements/notification/Notification';

// Import the new components
import ModalDeleteLog from './ModalDeleteLog';

const SystemLogsTable = ({ filterBy = '', activeKey }) => {
	// Define required states
	const { t } = useTranslation(); // useTranslation-Hook
	const [notification, setNotification] = useState({ variant: '', title: '', message: '' });
	const [showOffcanvas, setShowOffcanvas] = useState(false);
	const [selectedRow, setSelectedRow] = useState({});
	const [page, setPage] = useState(1);
	const [response, setData] = useState([]);
	const [isLoadingTable, setIsLoadingTable] = useState(true); // State for loading table

	// Methods / Functions
	const handleRowClick = (row, action) => {
		if (action === 'open') {
			setShowOffcanvas(true);
			fetchRowDetails(row); // Call API to fetch log details
		} else if (action === 'delete') {
			handleDeleteClick(row);
			setShowOffcanvas(false);
		}
	};

	// Delete Log
	const [showDeleteModal, setShowDeleteModal] = useState(false); // State for showing modal
	const [logToDelete, setLogToDelete] = useState(null); // State for API Credential to delete
	const [isLoadingButtonDeleteLog, setIsLoadingButtonDeleteLog] = useState(null); // State for loading spinner

	const handleDeleteClick = (log) => {
		setLogToDelete(log);
		setShowDeleteModal(true);
	};

	const handleConfirmDelete = async () => {
		if (logToDelete) {
			setIsLoadingButtonDeleteLog(true);
			const baseURL = process.env.REACT_APP_ENV === 'development'
				? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
				: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

			await axios.delete(`${baseURL}/logs/${logToDelete}`, options)
				.then(response => {
					setShowDeleteModal(false);
					setIsLoadingButtonDeleteLog(false);
					handleShowNotification(setNotification, 'success', 'Erfolgreich', t(response.data.message));
					//setData(response.filter(log => log.id !== logToDelete));
					// Refresh the table data after deletion
					getData(page); // Fetch the updated data
				})
				.catch((error) => {
					setShowDeleteModal(false);
					setIsLoadingButtonDeleteLog(false);
					const errorMessage = error.response?.data?.message ? error.message + '. ' + error.response.data.message : error.message;
					handleShowNotification(setNotification, 'danger', 'Fehler', errorMessage + '. Bitte versuche es erneut oder kontaktiere den HelpDesk.');
				});
		}
	};

	const fetchRowDetails = async (rowId) => {
		try {
			const response = await axios.get(`https://backend.haeppy.app/api/v1/logs/${rowId}`, options);
			const rowDetails = response.data;
			setSelectedRow(rowDetails.data); // Update selectedRow state with fetched log details
		} catch (error) {
			console.error(error);
		}
	};

	const handleCloseOffcanvas = () => setShowOffcanvas(false);

	// fetch data from api
	const authHeader = useAuthHeader();
	const options = {
		params: {
			page: page,
			limit: 1000
		},
		headers: {
			Authorization: authHeader,
			Accept: 'application/json'
		}
	};

	const getData = async (filterBy, pageNumber) => {
		setIsLoadingTable(true);

		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		await axios.get(`${baseURL}/logs?log_level=${filterBy}`, options)
			.then(response => {
				setData({data: response.data.data})
				setIsLoadingTable(false);
			})
			.catch((error) => {
				const errorMessage = error.response?.data?.message ? error.message + '. ' + error.response.data.message : error.message;
				handleShowNotification(setNotification, 'danger', 'Fehler', errorMessage + '. Bitte versuche es erneut oder kontaktiere den HelpDesk.');
				setIsLoadingTable(false);	
			})
	}

	useEffect(() => {
		getData(filterBy);
	}, [filterBy]);

	const CustomToggle = React.forwardRef(({ children, onClick }, ref) => (
		<Link
			to="#"
			ref={ref}
			onClick={(e) => {
				e.preventDefault();
				onClick(e);
			}}
			className="btn-icon btn btn-ghost btn-sm rounded-circle"
		>
			{children}
		</Link>
	));

	// 
	// Components
	//

	// Component ActionMenu
	const ActionMenu = (row) => {
		return (
			<Dropdown>
				<Dropdown.Toggle as={CustomToggle}>
					<MoreVertical size="15px" className="text-secondary" />
				</Dropdown.Toggle>
				<Dropdown.Menu align="end">
					<Dropdown.Header>{t('ACTION')}</Dropdown.Header>
					<Dropdown.Item eventKey="1" onClick={() => handleRowClick(row.ID, 'open')}>
						<Edit size="14px" className="me-1" /> {t('Show')}
					</Dropdown.Item>
					<Dropdown.Item eventKey="2" onClick={() => handleRowClick(row.ID, 'delete')}>
						<Trash size="14px" className="me-1" /> {t('Delete')}
					</Dropdown.Item>
				</Dropdown.Menu>
			</Dropdown>
		);
	};

	const logKey = (status) => {
		const badgeClass = status === 'backend-edge' ? 'primary' :
			(status === 'backend' ? 'primary' : 'primary');
		return <Badge className={`text-light-${badgeClass} bg-${badgeClass}`}>{status}</Badge>
	}

	const logLevel = (status) => {
		const badgeClass = status === 'info' ? 'info' :
			(status === 'warning' ? 'warning' :
				(status === 'error' ? 'danger' : 
					(status === 'heartbeat' ? 'secondary'
				: 'success')))
		return <Badge className={`text-${badgeClass} bg-light-${badgeClass}`}>{status}</Badge>
	}

	const columns = useMemo(
		() => [
			{
				accessorKey: 'id',
				header: t('ID'),
				cell: ({ getValue }) => {
					return (
						<Link className="fw-semibold" onClick={(event) => {
							event.preventDefault();
							handleRowClick(getValue(), 'open');
						}}>
							{getValue().toString().padStart(4, '0')} <i className="fe fe-arrow-right fs-4"></i>
						</Link>
					);
				}
			},
			{
				accessorKey: 'tenant_id',
				header: t('Tenant')
			},
			{
				accessorKey: 'log_timestamp',
				header: t('Timestamp'),
				cell: ({ getValue }) => {
					return (
						<span>
							<Moment utc={true} local={true} format="DD.MM.YYYY HH:mm:ss">
								{getValue()}
							</Moment>
						</span>
					);
				}
			},
			{
				accessorKey: 'log_key',
				header: t('System'),
				cell: ({ getValue }) => {
					return logKey(getValue());
				}
			},
			{
				accessorKey: 'log_level',
				header: t('Log Level'),
				cell: ({ getValue }) => {
					return logLevel(getValue());
				}
			},
			{
				accessorKey: 'log_value',
				header: t('Log Value'),
				size: 270, //set column size for this column
				cell: ({ getValue }) => {
					return <span>{JSON.stringify(getValue())}</span>;
				}
			},
			{
				accessorKey: 'action',
				header: t('Action'),
				cell: ({ row }) => {
					return <ActionMenu ID={row.original.id} />;
				}
			}
		],
		[]
	);

	const unfilteredData = useMemo(() => response?.data ?? [], [response.data]);
	//const pagination = useMemo(() => response?.pagination ?? [], [response.pagination]);

	// filtering....
	const data = useMemo(() => {
		return filterBy === '' ? unfilteredData : unfilteredData?.filter((item) => item.log_level === filterBy)
	}, [filterBy, unfilteredData]);

	if (isLoadingTable) {
		return (
			<Table className="text-nowrap">
				<thead >
					<tr>
						<th scope="col">{t('ID')}</th>
						<th scope="col">{t('Tenant')}</th>
						<th scope="col">{t('Timestamp')}</th>
						<th scope="col">{t('System')}</th>
						<th scope="col">{t('Log Level')}</th>
						<th scope="col">{t('Log Value')}</th>
					</tr>
				</thead>
				<tbody>
					{Array(10).fill(0).map((_, index) => (
						<tr key={index}>
							<td><Skeleton /></td>
							<td><Skeleton /></td>
							<td><Skeleton /></td>
							<td><Skeleton /></td>
							<td><Skeleton /></td>
							<td><Skeleton /></td>
						</tr>
					))}
				</tbody>
			</Table>

		);
	}

	return (
		<div>
			<TanstackTable
				data={data}
				columns={columns}
				filter={true}
				filterPlaceholder="Suche"
				pagination={true}
				exportButton={false}

			/>
			{selectedRow && Object.keys(selectedRow).length > 0 && (
				<Offcanvas show={showOffcanvas} onHide={handleCloseOffcanvas} placement="end">
					<Offcanvas.Header closeButton>
						<Offcanvas.Title><h2>{t('Details')}</h2></Offcanvas.Title>
					</Offcanvas.Header>
					<Offcanvas.Body>
						<div className="mb-4">
							<h4 className="mb-2">{t('ID')}</h4>
							<p>{selectedRow.id}</p>
						</div>
						<div className="mb-4">
							<h4 className="mb-2">{t('Tenant')}</h4>
							<p>{selectedRow.tenant_id}</p>
						</div>
						<div>
							<h4 className="mb-2">{t('Timestamp')}</h4>
							<p>
								<Moment utc={true} local={true} format="DD.MM.YYYY HH:mm:ss">{selectedRow.log_timestamp}</Moment>
							</p>
						</div>
						<div>
							<h4 className="mb-2">{t('System')}</h4>
							<p>{selectedRow.log_key}</p>
						</div>
						<div>
							<h4 className="mb-2">{t('Log Level')}</h4>
							<p>{selectedRow.log_level}</p>
						</div>
						<div>
							<h4 className="mb-2">{t('Log Value')}</h4>
							<p><code><pre>{JSON.stringify(selectedRow.log_value, null, 2)}</pre></code></p>
						</div>
					</Offcanvas.Body>
				</Offcanvas>
			)}
			{/* Delete Log Modal */}
			<ModalDeleteLog
				show={showDeleteModal}
				onHide={() => setShowDeleteModal(false)}
				handleConfirmDelete={handleConfirmDelete}
				isLoadingButton={isLoadingButtonDeleteLog}
			/>
		</div>
	);
};

export default SystemLogsTable;
